import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert, ProgressBar, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { updateOrdine } from '../services/api';
import toast from 'react-hot-toast';

const clienteLabel = (cliente) => {
  if (!cliente) return 'N/D';
  const { nome, cognome, denominazione_sociale } = cliente;
  const namePart = [nome, cognome].filter(Boolean).join(' ');
  return denominazione_sociale || namePart || 'N/D';
};

// Porta uno o piu' ordini a "Concluso" inserendo il tracking di ciascuno in un solo passaggio,
// cosi' non serve piu' aprire ogni ordine in modifica -> cambiare tab -> salvare.
const TrackingBatchModal = ({ show, onHide, ordini, onSuccess }) => {
  const [trackingValues, setTrackingValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processingStatus, setProcessingStatus] = useState('');

  useEffect(() => {
    if (show) {
      setError(null);
      setProcessingStatus('');
      setTrackingValues(
        ordini.reduce((acc, o) => {
          acc[o.id] = o.tracking_code || '';
          return acc;
        }, {})
      );
    }
  }, [show, ordini]);

  const handleTrackingChange = (id, value) => {
    setTrackingValues(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async () => {
    if (ordini.length === 0) {
      toast.error("Nessun ordine da concludere.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      for (let i = 0; i < ordini.length; i++) {
        const ordine = ordini[i];
        setProcessingStatus(`Aggiornamento ordine #${ordine.id} (${i + 1}/${ordini.length})...`);
        const payload = { stato: 'Concluso' };
        const tracking = (trackingValues[ordine.id] || '').trim();
        if (tracking) payload.tracking_code = tracking;
        await updateOrdine(ordine.id, payload);
      }
      toast.success(`${ordini.length} ordine${ordini.length > 1 ? 'i' : ''} conclus${ordini.length > 1 ? 'i' : 'o'} con successo.`);
      onHide();
      onSuccess();
    } catch (err) {
      console.error('Errore durante la conclusione degli ordini:', err);
      setError(err.response?.data?.detail || "Si è verificato un errore durante l'aggiornamento.");
    } finally {
      setLoading(false);
      setProcessingStatus('');
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size={ordini.length > 1 ? 'lg' : undefined}>
      <Modal.Header closeButton>
        <Modal.Title>
          Concludi {ordini.length === 1 ? `ordine #${ordini[0]?.id}` : `${ordini.length} ordini`} con tracking
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading && (
          <div className="mb-3">
            <small className="text-muted d-block mb-2">{processingStatus}</small>
            <ProgressBar animated now={100} variant="success" />
          </div>
        )}
        <Table size="sm" borderless className="mb-0">
          <tbody>
            {ordini.map(ordine => (
              <tr key={ordine.id}>
                <td className="align-middle" style={{ whiteSpace: 'nowrap' }}>
                  #{ordine.id}<br />
                  <small className="text-muted">{clienteLabel(ordine.cliente)}</small>
                </td>
                <td className="align-middle w-100">
                  <Form.Control
                    type="text"
                    placeholder="Codice tracking (opzionale)"
                    value={trackingValues[ordine.id] || ''}
                    onChange={(e) => handleTrackingChange(ordine.id, e.target.value)}
                    disabled={loading}
                  />
                </td>
                <td className="align-middle" style={{ whiteSpace: 'nowrap' }}>
                  <Link
                    to={`/ordini/edit/${ordine.id}`}
                    state={{ initialTab: 'spedizione' }}
                    className="small"
                  >
                    Apri scheda →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Annulla
        </Button>
        <Button variant="success" onClick={handleSubmit} disabled={loading}>
          {loading ? <Spinner animation="border" size="sm" /> : 'Concludi'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TrackingBatchModal;
